import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { createPortal } from "react-dom";
import { getDocuments, postDocumenResponse } from "../../../../Redux/Reducers/JobReducer";
import {
    FileText,
    CheckCircle2,
    AlertCircle,
    Eye,
    ExternalLink,
    X,
    Loader2,
    ChevronUp,
    ChevronDown,
    Check,
    XCircle,
    Download
} from "lucide-react";
import { toast } from "react-toastify";
// import AadhaarCard from "../../components/custom/AadharCard";
// import PanCard from "../../components/custom/PanCard";
import {
    Box,
    Paper,
    Typography,
    Button,
    IconButton,
    CircularProgress,
    TextareaAutosize,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
    Stack,
    Divider,
    Skeleton,
    useTheme,
    alpha
} from '@mui/material';
import { getVerifiedDocDataReducer } from "../../../../Redux/Reducers/VerifiedDocReducer";
import AadhaarCard from "../../../../Components/Custom/AadhaarCard";
import PanCard from "../../../../Components/Custom/PanCard";

const RequestedDocumentList = ({ applicant, userRefId }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const [docList, setDocList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [previewDoc, setPreviewDoc] = useState(null);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isSubmittingResponse, setIsSubmittingResponse] = useState(false);
    const [showRejectionModal, setShowRejectionModal] = useState(false);
    const [rejectionReason, setRejectionReason] = useState("");
    const application_id = applicant?.application_id;
    const [docData, setDocData] = useState("");
    const DocStatus = docData?.uploaded_docs_status;
    const applicantStatus = applicant?.application_status;
    const canOpenDocumentPreview = [
        "Offer Accepted",
        "Document Verification",
        "Document Verified"
    ].includes(applicantStatus);

    const [verifiedCardData, setVerifiedCardData] = useState(null);
    const [verifiedCardLoading, setVerifiedCardLoading] = useState(false);
 
    // console.log("requestedDocumentsNNNN", DocumentStatus)

    useEffect(() => {
        if (!previewDoc) {
            setVerifiedCardData(null);
            return;
        }

        const docName = previewDoc.file_name.toLowerCase();
        const isAadhar = docName.includes("aadhar") || docName.includes("aadhaar");
        const isPan = docName.includes("pan");

        if ((isAadhar || isPan) && userRefId) {
            const docType = isAadhar ? "aadhar" : "pan";
            setVerifiedCardLoading(true);
            dispatch(getVerifiedDocDataReducer({ token: userRefId, docType }))
                .unwrap()
                .then((res) => {
                    const actualData = res?.data?.data || res?.data || res || null;
                    setVerifiedCardData(actualData);
                })
                .catch((err) => {
                    console.error("Failed to fetch verified data for preview:", err);
                })
                .finally(() => {
                    setVerifiedCardLoading(false);
                });
        }
    }, [previewDoc, userRefId, dispatch]);

    const handleApprove = async () => {
        setIsSubmittingResponse(true);
        try {
            await dispatch(postDocumenResponse({
                id: application_id,
                action: "APPROVED"
            })).unwrap();
            toast.success("Documents approved successfully!");
            fetchDocuments();
        } catch (error) {
            console.error("Approval error:", error);
            toast.error(error?.message || "Failed to approve documents");
        } finally {
            setIsSubmittingResponse(false);
        }
    };

    const handleReject = async () => {
        if (!rejectionReason.trim()) {
            toast.error("Please provide a rejection reason");
            return;
        }
        setIsSubmittingResponse(true);
        try {
            await dispatch(postDocumenResponse({
                id: application_id,
                action: "REJECTED",
                rejection_reason: rejectionReason
            })).unwrap();
            toast.success("Documents rejected. Candidate notified.");
            setShowRejectionModal(false);
            setRejectionReason("");
            fetchDocuments();
        } catch (error) {
            console.error("Rejection error:", error);
            toast.error(error?.message || "Failed to reject documents");
        } finally {
            setIsSubmittingResponse(false);
        }
    };

    const fetchDocuments = async (showLoading = false) => {
        if (showLoading) setLoading(true);
        try {
            const res = await dispatch(getDocuments(application_id)).unwrap();
            setDocList(res?.data?.uploaded_docs || []);
            setDocData(res?.data || []);
        } catch (error) {
            console.error("Error fetching documents:", error);
        } finally {
            if (showLoading) setLoading(false);
        }
    };

    useEffect(() => {
        if (application_id) {
            fetchDocuments(true);
        }
    }, [application_id, dispatch]);

    const uploadedDocs = docList.filter(d => d.file_url);
    const pendingDocs = docList.filter(d => !d.file_url);

    const getStatusChip = () => {
        if (!DocStatus) return null;
        const statusMap = {
            "ACCEPTED": { label: "Approved", color: "success" },
            "APPROVED": { label: "Approved", color: "success" },
            "REJECTED": { label: "Rejected", color: "error" },
            "PENDING": { label: "Pending", color: "warning" },
            "UNDER_REVIEW": { label: "Under Review", color: "warning" }
        };
        const status = statusMap[DocStatus] || { label: DocStatus, color: "default" };
        return <Chip label={status.label} size="small" color={status.color} />;
    };

    const renderPreviewContent = () => {
        if (!previewDoc) return null;

        const docName = previewDoc.file_name.toLowerCase();
        const isAadhar = docName.includes("aadhar") || docName.includes("aadhaar");
        const isPan = docName.includes("pan");
        const hasVerifiedCard = (isAadhar || isPan) && verifiedCardData?.verifiedDetails;

        if (hasVerifiedCard) {
            return (
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3, width: '100%' }}>
                    {/* Left Column: Uploaded File Preview */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', mb: 1 }}>
                            Uploaded File / PDF Preview
                        </Typography>
                        {renderFilePreview()}
                    </Box>

                    {/* Right Column: Live Verified Card */}
                    <Box sx={{
                        flex: 1,
                        minWidth: 0,
                        borderTop: { xs: `1px solid ${theme.palette.divider}`, lg: 'none' },
                        borderLeft: { lg: `1px solid ${theme.palette.divider}` },
                        pt: { xs: 2, lg: 0 },
                        pl: { lg: 3 }
                    }}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', mb: 1 }}>
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                <Box sx={{ width: 6, height: 6, bgcolor: 'success.main', borderRadius: '50%', animation: 'pulse 1.5s infinite' }} />
                                Interactive DigiLocker Card
                            </Box>
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                            {verifiedCardLoading ? (
                                <Box sx={{ textAlign: 'center' }}>
                                    <CircularProgress size={24} sx={{ color: 'primary.main' }} />
                                    <Typography variant="caption" sx={{ display: 'block', mt: 1, fontWeight: 'bold', color: 'text.secondary' }}>
                                        Retrieving verified card...
                                    </Typography>
                                </Box>
                            ) : (
                                isAadhar ? (
                                    <AadhaarCard
                                        digiInfo={verifiedCardData.verifiedDetails}
                                        token={userRefId}
                                        docType="aadhar"
                                    />
                                ) : (
                                    <PanCard
                                        digiInfo={verifiedCardData.verifiedDetails}
                                        token={userRefId}
                                        docType="pan"
                                    />
                                )
                            )}
                        </Box>
                    </Box>
                </Box>
            );
        }

        return renderFilePreview();
    };

    const renderFilePreview = () => {
        if (!previewDoc?.file_url) {
            return (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <AlertCircle size={32} style={{ color: theme.palette.text.disabled, margin: '0 auto' }} />
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, fontWeight: 'bold', color: 'text.secondary' }}>
                        No file preview available
                    </Typography>
                </Box>
            );
        }

        const cleanUrl = previewDoc.file_url.split('?')[0].toLowerCase();
        const isImage = cleanUrl.endsWith('.jpg') || cleanUrl.endsWith('.jpeg') || cleanUrl.endsWith('.png') || cleanUrl.endsWith('.webp') || cleanUrl.endsWith('.gif');
        const isPdf = cleanUrl.endsWith('.pdf');

        if (isImage) {
            return (
                <Box
                    component="img"
                    src={previewDoc.file_url}
                    alt={previewDoc.file_name}
                    sx={{
                        maxWidth: '100%',
                        maxHeight: '55vh',
                        objectFit: 'contain',
                        borderRadius: 1,
                        border: `1px solid ${theme.palette.divider}`,
                        boxShadow: 1
                    }}
                />
            );
        } else {
            return (
                <Box
                    component="iframe"
                    src={isPdf ? `${previewDoc.file_url}#toolbar=0` : previewDoc.file_url}
                    title={previewDoc.file_name}
                    sx={{
                        width: '100%',
                        height: '55vh',
                        borderRadius: 1,
                        border: `1px solid ${theme.palette.divider}`,
                        bgcolor: 'background.paper'
                    }}
                />
            );
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3, bgcolor: 'action.hover', borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                <CircularProgress size={24} sx={{ color: 'primary.main' }} />
            </Box>
        );
    }

    if (docList.length === 0) {
        return null;
    }

    return (
        <Paper sx={{ p: 2.5, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, boxShadow: 1 }}>
            {/* Panel Header */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                pb: isCollapsed ? 0 : 1.5,
                borderBottom: isCollapsed ? 'none' : `1px solid ${theme.palette.divider}`
            }}>
                <Box
                    onClick={() => setIsCollapsed(!isCollapsed)}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        cursor: 'pointer',
                        userSelect: 'none',
                        '&:hover': {
                            '& .MuiTypography-root': { color: 'text.primary' }
                        }
                    }}
                >
                    <FileText size={18} style={{ color: theme.palette.primary.main }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                        {uploadedDocs?.length > 0 ? "Submitted Documents" : "Requested Documents"}
                    </Typography>
                    <Box sx={{ color: 'text.disabled' }}>
                        {isCollapsed ? <ChevronDown size={14} strokeWidth={2.5} /> : <ChevronUp size={14} strokeWidth={2.5} />}
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getStatusChip()}
                    <Chip
                        label={`${uploadedDocs?.length} / ${docList?.length} Uploaded`}
                        size="small"
                        sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            color: 'primary.main',
                            fontWeight: 'bold',
                            fontSize: '0.625rem'
                        }}
                    />
                </Box>
            </Box>

            {/* Document Items List */}
            {!isCollapsed && (
                <>
                    {/* Candidate Consent Card */}
                    {(() => {
                        const topLevelRemark = docData?.candidate_remarks || docData?.user_remarks || docData?.consent_remarks || docData?.remarks || docData?.consent || docData?.comment || docData?.comments;
                        const docLevelItem = docList.find(d => d.candidate_remarks || d.user_remarks || d.consent_remarks || d.remarks);
                        const docLevelRemark = docLevelItem?.candidate_remarks || docLevelItem?.user_remarks || docLevelItem?.consent_remarks || docLevelItem?.remarks;
                        const remark = topLevelRemark || docLevelRemark;
                        if (!remark) return null;
                        return (
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 1.5,
                                p: 1.75,
                                borderRadius: 2,
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                bgcolor: alpha(theme.palette.primary.main, 0.04),
                                mt: 2
                            }}>
                                <Box sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: 1,
                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    mt: 0.5
                                }}>
                                    <FileText size={14} style={{ color: theme.palette.primary.main }} />
                                </Box>
                                <Box>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'primary.main', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block' }}>
                                        Candidate Consent
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 'medium', mt: 0.25 }}>
                                        {remark}
                                    </Typography>
                                </Box>
                            </Box>
                        );
                    })()}

                    <Box
                        sx={{
                            mt: 1.5,
                            maxHeight: 340,
                            overflowY: 'auto',
                            pr: 0.5,
                            '& > *:not(:last-child)': { mb: 1.5 },
                            '&::-webkit-scrollbar': { width: 6 },
                            '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
                            '&::-webkit-scrollbar-thumb': {
                                bgcolor: alpha(theme.palette.text.primary, 0.15),
                                borderRadius: 3,
                            },
                        }}
                    >
                        {/* Uploaded Documents */}
                        {uploadedDocs?.map((doc, idx) => (
                            <Box
                                key={`uploaded-${idx}`}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    p: 1.5,
                                    borderRadius: 2,
                                    border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                                    bgcolor: alpha(theme.palette.success.main, 0.04),
                                    '&:hover': {
                                        borderColor: alpha(theme.palette.success.main, 0.4)
                                    }
                                }}
                            >
                                <Box sx={{ minWidth: 0, pr: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.primary', truncate: 'ellipsis' }}>
                                            {doc.file_name}
                                            {(doc.is_mandatory === true || doc.is_mandatory === 1 || doc.is_mandatory === 'true' || doc.is_mandatory === '1') && (
                                                <Box component="span" sx={{ color: 'error.main', fontWeight: 'bold', ml: 0.25 }}>*</Box>
                                            )}
                                        </Typography>
                                        {(doc.is_mandatory === true || doc.is_mandatory === 1 || doc.is_mandatory === 'true' || doc.is_mandatory === '1') && (
                                            <Chip
                                                label="Mandatory"
                                                size="small"
                                                sx={{
                                                    height: 18,
                                                    fontSize: '0.5625rem',
                                                    fontWeight: 'bold',
                                                    bgcolor: alpha(theme.palette.error.main, 0.08),
                                                    color: 'error.main',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px'
                                                }}
                                            />
                                        )}
                                        <Chip
                                            label="Received"
                                            size="small"
                                            icon={<CheckCircle2 size={10} style={{ color: theme.palette.success.main }} />}
                                            sx={{
                                                height: 18,
                                                fontSize: '0.5625rem',
                                                fontWeight: 'bold',
                                                bgcolor: alpha(theme.palette.success.main, 0.08),
                                                color: 'success.main',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px'
                                            }}
                                        />
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                                    <Button
                                        size="small"
                                        onClick={() => {
                                            if (!canOpenDocumentPreview) return;
                                            setPreviewDoc(doc);
                                        }}
                                        disabled={!canOpenDocumentPreview}
                                        startIcon={<Eye size={12} />}
                                        sx={{
                                            fontSize: '0.5625rem',
                                            fontWeight: 'bold',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            borderColor: 'divider',
                                            color: canOpenDocumentPreview ? 'text.secondary' : 'text.disabled',
                                            '&:hover': {
                                                borderColor: canOpenDocumentPreview ? alpha(theme.palette.primary.main, 0.3) : 'divider',
                                                color: canOpenDocumentPreview ? 'primary.main' : 'text.disabled'
                                            }
                                        }}
                                        variant="outlined"
                                    >
                                        View
                                    </Button>
                                </Box>
                            </Box>
                        ))}

                        {/* Pending Documents */}
                        {/* {pendingDocs.map((doc, idx) => (
                            <Box
                                key={`pending-${idx}`}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    p: 1.5,
                                    borderRadius: 2,
                                    border: `1px solid ${theme.palette.divider}`,
                                    bgcolor: alpha(theme.palette.action.hover, 0.3),
                                    '&:hover': {
                                        borderColor: alpha(theme.palette.text.secondary, 0.2)
                                    }
                                }}
                            >
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.disabled' }}>
                                            {doc.file_name}
                                            {(doc.is_mandatory === true || doc.is_mandatory === 1 || doc.is_mandatory === 'true' || doc.is_mandatory === '1') && (
                                                <Box component="span" sx={{ color: 'error.main', fontWeight: 'bold', ml: 0.25 }}>*</Box>
                                            )}
                                        </Typography>
                                        {(doc.is_mandatory === true || doc.is_mandatory === 1 || doc.is_mandatory === 'true' || doc.is_mandatory === '1') && (
                                            <Chip
                                                label="Mandatory"
                                                size="small"
                                                sx={{
                                                    height: 18,
                                                    fontSize: '0.5625rem',
                                                    fontWeight: 'bold',
                                                    bgcolor: alpha(theme.palette.error.main, 0.08),
                                                    color: 'error.main',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px'
                                                }}
                                            />
                                        )}
                                        <Chip
                                            label="Awaiting"
                                            size="small"
                                            icon={<AlertCircle size={10} style={{ color: theme.palette.text.disabled }} />}
                                            sx={{
                                                height: 18,
                                                fontSize: '0.5625rem',
                                                fontWeight: 'bold',
                                                bgcolor: alpha(theme.palette.text.disabled, 0.08),
                                                color: 'text.disabled',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px'
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        ))} */}
                        {pendingDocs?.map((doc, idx) => {
                            const isMandatory =
                                doc.is_mandatory === true || doc.is_mandatory === 1 ||
                                doc.is_mandatory === 'true' || doc.is_mandatory === '1';

                            return (
                                <Box
                                    key={`pending-${idx}`}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        p: 1.5,
                                        borderRadius: 2,
                                        border: `1.5px dashed ${isMandatory ? alpha(theme.palette.error.main, 0.4) : theme.palette.divider}`,
                                        bgcolor: isMandatory ? alpha(theme.palette.error.main, 0.03) : 'background.paper',
                                        '&:hover': {
                                            borderColor: isMandatory
                                                ? alpha(theme.palette.error.main, 0.6)
                                                : alpha(theme.palette.text.secondary, 0.35),
                                        },
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                                        <Box
                                            sx={{
                                                width: 28,
                                                height: 28,
                                                borderRadius: 1,
                                                bgcolor: isMandatory
                                                    ? alpha(theme.palette.error.main, 0.08)
                                                    : alpha(theme.palette.text.disabled, 0.1),
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0,
                                            }}
                                        >
                                            <AlertCircle
                                                size={14}
                                                style={{ color: isMandatory ? theme.palette.error.main : theme.palette.text.disabled }}
                                            />
                                        </Box>

                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                                                {doc.file_name}
                                                {isMandatory && (
                                                    <Box component="span" sx={{ color: 'error.main', fontWeight: 'bold', ml: 0.25 }}>*</Box>
                                                )}
                                            </Typography>
                                            {isMandatory && (
                                                <Chip
                                                    label="Mandatory"
                                                    size="small"
                                                    sx={{
                                                        height: 18,
                                                        fontSize: '0.5625rem',
                                                        fontWeight: 'bold',
                                                        bgcolor: alpha(theme.palette.error.main, 0.08),
                                                        color: 'error.main',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.5px',
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    </Box>

                                    <Chip
                                        label="Awaiting"
                                        size="small"
                                        icon={<AlertCircle size={10} style={{ color: theme.palette.text.disabled }} />}
                                        sx={{
                                            height: 18,
                                            fontSize: '0.5625rem',
                                            fontWeight: 'bold',
                                            bgcolor: alpha(theme.palette.text.disabled, 0.08),
                                            color: 'text.disabled',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            flexShrink: 0,
                                        }}
                                    />
                                </Box>
                            );
                        })}
                    </Box>

                    {/* Action Buttons at the Bottom */}
                    {uploadedDocs?.length > 0 && (!DocStatus || DocStatus === "PENDING" || DocStatus === "UNDER_REVIEW" || DocStatus === "REJECTED") && (
                        <Box sx={{ pt: 2, mt: 1, borderTop: `1px solid ${theme.palette.divider}`, display: 'flex', gap: 1.5 }}>
                            <Button
                                onClick={() => setShowRejectionModal(true)}
                                disabled={isSubmittingResponse || loading}
                                startIcon={<XCircle size={14} />}
                                sx={{
                                    flex: 1,
                                    fontSize: '0.625rem',
                                    fontWeight: 'bold',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    borderColor: alpha(theme.palette.error.main, 0.3),
                                    color: 'error.main',
                                    '&:hover': {
                                        borderColor: alpha(theme.palette.error.main, 0.5),
                                        bgcolor: alpha(theme.palette.error.main, 0.04)
                                    }
                                }}
                                variant="outlined"
                            >
                                Reject Docs
                            </Button>
                            <Button
                                onClick={handleApprove}
                                disabled={isSubmittingResponse || loading}
                                startIcon={isSubmittingResponse ? <CircularProgress size={14} sx={{ color: 'common.white' }} /> : <Check size={14} />}
                                sx={{
                                    flex: 1,
                                    fontSize: '0.625rem',
                                    fontWeight: 'bold',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    bgcolor: 'success.main',
                                    color: 'common.white',
                                    '&:hover': {
                                        bgcolor: 'success.dark'
                                    },
                                    '&:disabled': {
                                        bgcolor: alpha(theme.palette.success.main, 0.5)
                                    }
                                }}
                                variant="contained"
                            >
                                {isSubmittingResponse ? 'Processing...' : 'Approve Docs'}
                            </Button>
                        </Box>
                    )}

                    {/* Status Display when already approved */}
                    {(DocStatus === "ACCEPTED" || DocStatus === "APPROVED") && (
                        <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.divider}`, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CheckCircle2 size={16} style={{ color: theme.palette.success.main, flexShrink: 0 }} />
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                Documents Approved & Verified
                            </Typography>
                        </Box>
                    )}

                    {/* Status Display when already rejected */}
                    {DocStatus === "REJECTED" && (
                        <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.divider}`, '& > *:not(:last-child)': { mb: 1 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <XCircle size={16} style={{ color: theme.palette.error.main, flexShrink: 0 }} />
                                <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                                    Documents Rejected
                                </Typography>
                            </Box>
                            {docData?.uploaded_docs_rejection_reason && (
                                <Box sx={{
                                    p: 1.25,
                                    bgcolor: alpha(theme.palette.action.hover, 0.3),
                                    border: `1px solid ${theme.palette.divider}`,
                                    borderRadius: 1
                                }}>
                                    <Typography variant="caption" sx={{ fontWeight: 'semibold', color: 'text.secondary' }}>
                                        Reason: {docData.uploaded_docs_rejection_reason}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}
                </>
            )}

            {/* Document Preview Modal */}
            {previewDoc && createPortal(
                <Dialog
                    open={!!previewDoc}
                    onClose={() => setPreviewDoc(null)}
                    maxWidth={(() => {
                        const docName = previewDoc.file_name.toLowerCase();
                        const isAadhar = docName.includes("aadhar") || docName.includes("aadhaar");
                        const isPan = docName.includes("pan");
                        return (isAadhar || isPan) && verifiedCardData?.verifiedDetails ? 'xl' : 'lg';
                    })()}
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: 2,
                            maxHeight: '85vh',
                            overflow: 'hidden'
                        }
                    }}
                >
                    {/* Modal Header */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 2.5,
                        py: 2,
                        borderBottom: `1px solid ${theme.palette.divider}`
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FileText size={16} style={{ color: theme.palette.primary.main }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                {previewDoc.file_name}
                            </Typography>
                        </Box>
                        <IconButton size="small" onClick={() => setPreviewDoc(null)}>
                            <X size={16} />
                        </IconButton>
                    </Box>

                    {/* Modal Body */}
                    <Box sx={{
                        flex: 1,
                        bgcolor: alpha(theme.palette.action.hover, 0.3),
                        p: 2.5,
                        overflow: 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: 300
                    }}>
                        {renderPreviewContent()}
                    </Box>

                    {/* Modal Footer */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 1.5,
                        px: 2.5,
                        py: 2,
                        borderTop: `1px solid ${theme.palette.divider}`,
                        bgcolor: alpha(theme.palette.action.hover, 0.2)
                    }}>
                        <Button
                            href={previewDoc.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            startIcon={<Download size={12} />}
                            sx={{
                                fontSize: '0.625rem',
                                fontWeight: 'bold',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}
                            variant="outlined"
                        >
                            Download
                        </Button>
                        <Button
                            onClick={() => setPreviewDoc(null)}
                            sx={{
                                fontSize: '0.625rem',
                                fontWeight: 'bold',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}
                            variant="contained"
                        >
                            Close
                        </Button>
                    </Box>
                </Dialog>,
                document.body
            )}

            {/* Rejection Reason Modal */}
            {showRejectionModal && createPortal(
                <Dialog
                    open={showRejectionModal}
                    onClose={() => setShowRejectionModal(false)}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: { borderRadius: 2 }
                    }}
                >
                    <DialogTitle sx={{ borderBottom: `1px solid ${theme.palette.divider}`, px: 2.5, py: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AlertCircle size={18} style={{ color: theme.palette.error.main }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                Reject Document Submission
                            </Typography>
                        </Box>
                    </DialogTitle>

                    <DialogContent sx={{ px: 2.5, py: 3 }}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', mb: 1 }}>
                            Reason for Rejection
                        </Typography>
                        <TextareaAutosize
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                            placeholder="Describe why you are rejecting the documents (e.g., Aadhar Card image is blurry or expired)..."
                            minRows={4}
                            style={{
                                width: '100%',
                                padding: '12px',
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: '8px',
                                fontSize: '0.75rem',
                                fontFamily: 'inherit',
                                outline: 'none',
                                resize: 'vertical',
                                transition: 'all 0.2s',
                                color: theme.palette.text.primary,
                                backgroundColor: theme.palette.background.paper
                            }}
                            onFocus={(e) => {
                                e.target.style.borderColor = theme.palette.primary.main;
                                e.target.style.boxShadow = `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`;
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = theme.palette.divider;
                                e.target.style.boxShadow = 'none';
                            }}
                        />
                    </DialogContent>

                    <DialogActions sx={{
                        borderTop: `1px solid ${theme.palette.divider}`,
                        px: 2.5,
                        py: 2,
                        bgcolor: alpha(theme.palette.action.hover, 0.2)
                    }}>
                        <Button
                            onClick={() => setShowRejectionModal(false)}
                            sx={{
                                fontSize: '0.625rem',
                                fontWeight: 'bold',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleReject}
                            disabled={isSubmittingResponse || !rejectionReason.trim()}
                            variant="contained"
                            sx={{
                                fontSize: '0.625rem',
                                fontWeight: 'bold',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                bgcolor: 'error.main',
                                '&:hover': {
                                    bgcolor: 'error.dark'
                                }
                            }}
                        >
                            {isSubmittingResponse ? <CircularProgress size={14} sx={{ color: 'common.white' }} /> : 'Confirm Rejection'}
                        </Button>
                    </DialogActions>
                </Dialog>,
                document.body
            )}
        </Paper>
    );
};

export default RequestedDocumentList;
